import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_MAX_LIMIT,
  type BankAccountOut,
  type BankAccountStatementOut,
  type Page,
} from '@/shared/api/types'

/** Tipos de conta (BankAccountCreate.kind). */
export const ACCOUNT_KINDS = ['checking', 'savings', 'investment', 'cash', 'other'] as const
export type AccountKind = (typeof ACCOUNT_KINDS)[number]

export interface BankOut {
  readonly id: string
  readonly code: string
  readonly name: string
}

/** Saldo por conta — NÃO é Page (soma, não contagem). */
export interface BalancesOut {
  readonly items: readonly {
    readonly bank_account: { id: string; name: string }
    balance: string
  }[]
  readonly total_balance: string
}

export const bankAccountKeys = {
  all: () => ['bank-accounts'] as const,
  lists: () => [...bankAccountKeys.all(), 'list'] as const,
  balances: () => [...bankAccountKeys.all(), 'balances'] as const,
  statement: (accountId: string, p: BankAccountStatementParams) =>
    [...bankAccountKeys.all(), 'statement', accountId, p] as const,
} as const

/** Faixa do extrato da conta: `from`/`to` inclusivos (ausente = default do servidor). */
export interface BankAccountStatementParams {
  readonly from?: string
  readonly to?: string
  readonly limit?: number
  readonly offset?: number
}

/**
 * Extrato COM SALDO CORRENTE de uma conta: `GET /v1/bank-accounts/{id}/statement`.
 *
 * O envelope traz `opening_balance_at_from` (saldo no início de `from`),
 * `closing_balance_at_to` e os `entries` PAGINADOS em ordem cronológica FIXA
 * (`paid_date` ASC, desempate por id). O saldo corrente por linha e a série
 * diária do gráfico são derivados no cliente (account-statement-model) — o
 * backend não devolve running balance por linha (decisão de MVP).
 *
 * Pedimos o teto de linhas (`PAGE_MAX_LIMIT`) por padrão: para o saldo acumulado
 * bater ponta a ponta o mês inteiro precisa vir numa página só. Meses com mais
 * de 1000 lançamentos (raro) truncariam o acúmulo — some no cliente, não no
 * servidor. A CHAVE mora sob `bank-accounts`: assim tanto criar lançamento
 * quanto transferir (que já invalidam `bankAccountKeys.all()`) atualizam o
 * extrato sem fiação extra.
 */
export const bankAccountStatementQueryOptions = (
  accountId: string,
  p: BankAccountStatementParams = {},
) =>
  queryOptions({
    queryKey: bankAccountKeys.statement(accountId, p),
    queryFn: ({ signal }) =>
      api.get<BankAccountStatementOut>(
        `/v1/bank-accounts/${accountId}/statement`,
        {
          ...(p.from !== undefined && { from: p.from }),
          ...(p.to !== undefined && { to: p.to }),
          limit: p.limit ?? PAGE_MAX_LIMIT,
          ...(p.offset !== undefined && { offset: p.offset }),
        },
        signal,
      ),
    // Navegar de mês mantém o mês anterior na tela até o novo chegar (sem flash).
    placeholderData: keepPreviousData,
  })

export const banksQueryOptions = () =>
  queryOptions({
    // Catálogo GLOBAL, imutável — cacheia forte.
    queryKey: ['banks'],
    queryFn: ({ signal }) => api.get<Page<BankOut>>('/v1/banks', { limit: PAGE_MAX_LIMIT }, signal),
    staleTime: Infinity,
  })

export const bankAccountsQueryOptions = () =>
  queryOptions({
    queryKey: bankAccountKeys.lists(),
    queryFn: ({ signal }) =>
      api.get<Page<BankAccountOut>>('/v1/bank-accounts', { order_by: 'name' }, signal),
    staleTime: 60_000,
  })

/** Saldo de todas as contas + total. Endpoint separado, sem paginação. */
export const balancesQueryOptions = () =>
  queryOptions({
    queryKey: bankAccountKeys.balances(),
    queryFn: ({ signal }) => api.get<BalancesOut>('/v1/bank-accounts/balances', undefined, signal),
    staleTime: 30_000,
  })

/**
 * BankAccountCreate: name + kind obrigatórios; bank_id, agency, account_number,
 * opening_balance opcionais. opening_balance é o saldo INICIAL da conta (o quanto
 * já havia nela quando você começou a controlar aqui) — dinheiro como string.
 */
export const bankAccountFormSchema = z.object({
  name: z.string().trim().min(1, 'validation:required').max(120, 'validation:maxLength'),
  kind: z.enum(ACCOUNT_KINDS),
  bankId: z.string(), // '' = sem banco (caixa interno)
  agency: z.string().trim().max(20),
  accountNumber: z.string().trim().max(30),
  openingBalance: z.string(), // '' = 0
})

export type BankAccountFormValues = z.infer<typeof bankAccountFormSchema>

/** Form → corpo da API (compartilhado por criar e editar). */
function toBody(v: BankAccountFormValues): Record<string, string> {
  return {
    name: v.name,
    kind: v.kind,
    ...(v.bankId !== '' && { bank_id: v.bankId }),
    ...(v.agency !== '' && { agency: v.agency }),
    ...(v.accountNumber !== '' && { account_number: v.accountNumber }),
    ...(v.openingBalance !== '' && { opening_balance: v.openingBalance }),
  }
}

/** POST /v1/bank-accounts — cadastro, sem Idempotency-Key. */
export function createBankAccount(v: BankAccountFormValues): Promise<BankAccountOut> {
  return api.post<BankAccountOut>('/v1/bank-accounts', { body: toBody(v) }).then((r) => r.data)
}

/**
 * PATCH /v1/bank-accounts/{id} — edição. Aceita name, kind, banco, agência,
 * conta e opening_balance (o SALDO INICIAL; o saldo atual é derivado dos
 * lançamentos e não é editável).
 */
export function updateBankAccount(id: string, v: BankAccountFormValues): Promise<BankAccountOut> {
  return api.patch<BankAccountOut>(`/v1/bank-accounts/${id}`, toBody(v))
}

/**
 * DELETE /v1/bank-accounts/{id} — SOFT delete.
 * 409 se a conta tem movimento (lançamentos): a tela orienta.
 */
export function deleteBankAccount(id: string): Promise<void> {
  return api.del(`/v1/bank-accounts/${id}`)
}
